import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { Section, Container } from 'components/base';
import { CloudinaryPicture, HtmlContent } from 'components/rich';
import useDataContext from 'data/context/data';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { couponSale } from 'ecommerce/lib/coupon';

import Breadcrumb from '../breadcrumb';
import { ProductDetailLink } from '../pageLinks';
import CatalogProductCard from '../custom/catalogProductCard';
import NewsletterTile from '../newsletter/newsletterTile';
import PdpIcon from './pdpIcon';
import ProductDetails from './productDetails';
import ProductFaq from './productFaq';
import StickyProductBar from './stickyProductBar';

// Matches .pdp-related-cards: 4 columns, 2 columns, a 72% wide swipe row on phones.
const RELATED_CARD_SIZES = '(min-width: 992px) 25vw, (min-width: 576px) 50vw, 72vw';

const SECTION_IDS = {
  details: 'details',
  reviews: 'reviews',
  faq: 'faq',
  related: 'you-might-like',
};

// Matches the sticky bar's active-section line (stickyProductBar.js), so a tab
// jump to the first section keeps the bar on screen.
const BAR_LINE_PX = 96;
// Bootstrap lg, where the bar moves from the bottom to the top (_product-page.scss).
const DESKTOP_QUERY = '(min-width: 992px)';

// Shows the sticky bar once the sentinel (end of the top section, below the
// picker and its Continue button) has scrolled up to the bar. A scroll
// check rather than IntersectionObserver: a jump from the page end back to the
// top never intersects the sentinel, so the observer would not fire.
const usePassedSentinel = () => {
  const ref = useRef(null);
  const [passed, setPassed] = useState(false);

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      // The mobile bar sits at the bottom, it may show as soon as the picker is gone.
      const line = window.matchMedia(DESKTOP_QUERY).matches ? BAR_LINE_PX : 0;
      setPassed(ref.current.getBoundingClientRect().top < line);
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return [ref, passed];
};

const Eyebrow = ({ children }) => <p className="pdp-eyebrow">{children}</p>;

Eyebrow.propTypes = {
  children: PropTypes.node,
};

const Testimonial = ({ testimonial }) => (
  <figure className="pdp-testimonial">
    {testimonial.imageKey && (
      <div className="pdp-testimonial-photo">
        <CloudinaryPicture prefix="products/" imageKey={testimonial.imageKey} loading="lazy" />
      </div>
    )}
    <div className="pdp-testimonial-copy">
      <h2 className="pdp-eyebrow">From our customers</h2>
      <PdpIcon name="quote" size={40} className="pdp-testimonial-mark" />
      <blockquote className="pdp-testimonial-quote">
        <p>{testimonial.quote}</p>
      </blockquote>
      <figcaption className="pdp-testimonial-author">
        {testimonial.source?.toLowerCase() === 'instagram' && <PdpIcon name="instagram" />}
        <span className="pdp-testimonial-handle">{testimonial.author}</span>
        {testimonial.source && <span>{`on ${testimonial.source}`}</span>}
      </figcaption>
    </div>
  </figure>
);

Testimonial.propTypes = {
  testimonial: PropTypes.shape({
    quote: PropTypes.string,
    author: PropTypes.string,
    source: PropTypes.string,
    imageKey: PropTypes.string,
  }).isRequired,
};

/**
 * Redesigned PDP layout below the top section, for `format: 2` content files
 * (docs/product-page.md). The top section is passed as children and
 * rendered unchanged. Optional sections render only when their data exists.
 */
const ProductSections = ({
  product,
  content,
  linkedContent = [],
  breadcrumb,
  buyAction,
  summary,
  price,
  children,
}) => {
  const dataContext = useDataContext();
  const claimedCoupon = useClaimedCoupon();
  const [sentinelRef, pickerPassed] = usePassedSentinel();

  const contentById = Object.fromEntries(linkedContent.map((item) => [item.id, item]));
  const withContent = (ids) =>
    dataContext
      .getProducts(ids ?? [])
      .filter((linkedProduct) => contentById[linkedProduct.id])
      .map((linkedProduct) => ({ product: linkedProduct, content: contentById[linkedProduct.id] }));

  const pairs = withContent(content.pairsWith);
  const related = withContent(product.related);
  const hasTestimonial = Boolean(content.testimonial?.quote);
  const hasFaq = content.faq?.length > 0;

  const tabs = [
    { id: SECTION_IDS.details, label: 'Details' },
    hasTestimonial && { id: SECTION_IDS.reviews, label: 'Reviews' },
    hasFaq && { id: SECTION_IDS.faq, label: 'FAQ' },
    related.length > 0 && { id: SECTION_IDS.related, label: 'You might like' },
  ].filter(Boolean);

  return (
    <div className="pdp">
      <Section className="section-product">
        <Container>
          {breadcrumb && <Breadcrumb items={breadcrumb} />}
          {children}
        </Container>
      </Section>
      <div ref={sentinelRef} aria-hidden="true" />

      {buyAction && (
        <StickyProductBar
          visible={pickerPassed}
          tabs={tabs}
          summary={summary}
          price={price}
          action={buyAction}
        />
      )}

      <section id={SECTION_IDS.details} className="pdp-section">
        <Container>
          <div className="pdp-section-head">
            {content.detailsTitle && <Eyebrow>Product details</Eyebrow>}
            <h2 className="pdp-h2">{content.detailsTitle ?? 'Product details'}</h2>
          </div>
          <ProductDetails productId={product.id} content={content} pairs={pairs} />
        </Container>
      </section>

      {hasTestimonial && (
        <section id={SECTION_IDS.reviews} className="pdp-section pdp-section-orange">
          <Container>
            <Testimonial testimonial={content.testimonial} />
          </Container>
        </section>
      )}

      {hasFaq && (
        <section id={SECTION_IDS.faq} className="pdp-section pdp-section-green">
          <Container>
            <ProductFaq caption={content.caption} items={content.faq} />
          </Container>
        </section>
      )}

      {related.length > 0 && (
        <section id={SECTION_IDS.related} className="pdp-section pdp-section-brown">
          <Container>
            <div className="pdp-section-head pdp-related-head">
              <div>
                {content.relatedTitle && <Eyebrow>You might like</Eyebrow>}
                <h2 className="pdp-h2">{content.relatedTitle ?? 'You might like'}</h2>
              </div>
              {content.relatedLink && (
                <Link href={content.relatedLink.url} className="pdp-arrow-link">
                  {content.relatedLink.label}
                  <PdpIcon name="arrowRight" size={14} />
                </Link>
              )}
            </div>
            <div className="pdp-related-cards">
              {related.map(({ product: relatedProduct, content: relatedContent }) => (
                <ProductDetailLink key={relatedProduct.id} productId={relatedProduct.id}>
                  <CatalogProductCard
                    product={relatedProduct}
                    meta={relatedContent.cardMeta}
                    badge={relatedContent.badge}
                    sale={couponSale(claimedCoupon.previewSummary, relatedProduct)}
                    sizes={RELATED_CARD_SIZES}
                  />
                </ProductDetailLink>
              ))}
            </div>
          </Container>
        </section>
      )}

      {content.extraDescription && (
        <section className="pdp-section pdp-section-ruled">
          <Container>
            <HtmlContent className="pdp-seo-copy" html={content.extraDescription} />
          </Container>
        </section>
      )}

      <NewsletterTile />
    </div>
  );
};

ProductSections.propTypes = {
  product: PropTypes.object.isRequired,
  content: PropTypes.object.isRequired,
  // Content entries of the products in pairsWith and related.
  linkedContent: PropTypes.arrayOf(PropTypes.object),
  // Home > category > product items, shared with the BreadcrumbList JSON-LD.
  breadcrumb: PropTypes.arrayOf(PropTypes.object),
  // The top section's buy button (Continue / Add to Cart), repeated in the sticky bar.
  buyAction: PropTypes.node,
  summary: PropTypes.string,
  price: PropTypes.node,
  children: PropTypes.node,
};

export default ProductSections;
